import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import "./complete-purchase.scss";
import { Effect } from '../../assets/svgIcon/customIcon';
import Button from "../../components/command/Button/Button";
import { API_ENDPOINTS } from '../../data/api-endpoints';
import { http, HttpClient } from '../../data/http-client';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Info } from 'react-feather';

interface OwnItDetails {
  discountPercentage: number;
  productRetailPrice: number;
  ownItPrice: number;
  depositPaid: number;
  rentalTotalToPayToday: number;
}

interface OrderProduct {
  id: number;
  uuid: string;
  productUuid: string;
  productName: string;
  productQuantity: number;
  productRate: string;
  productRetailValue: string;
  option1?: string;
  option2?: string;
  option3?: string;
  productCondition?: number;
}

interface PaymentSessionResponse {
  success: boolean;
  orderUuid: string;
  customerUuid: string;
  token: string;
  expiresIn: string;
}

interface OrderResponse {
  success: boolean;
  data: {
    order: {
      uuid: string;
      customerUuid: string;
      // Add other order properties as needed
      [key: string]: any;
    };
    orderProducts: OrderProduct[];
    ownItDetails: OwnItDetails;
    ownItDetailsForRedispatch?: OwnItDetails;
    isAvailableForRedispatch: boolean;
  };
}

const CompletePurchase = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Immediate redirect if no ID is provided
  useEffect(() => {
    if (!id) {
      navigate('/not-found', { replace: true });
      return;
    }
  }, [id, navigate]);

  const [orderData, setOrderData] = useState<OrderResponse['data'] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentSession, setPaymentSession] = useState<PaymentSessionResponse | null>(null);
  const [selectedOption, setSelectedOption] = useState<'preused' | 'new'>('preused');

  // Function to handle payment session creation
  const handlePaymentSession = async (option: 'preused' | 'new') => {
    if (!orderData?.order?.customerUuid || !orderData?.order?.uuid) return;
    
    const isRequireRedispatch = option === 'new';
    const details = isRequireRedispatch && orderData.ownItDetailsForRedispatch
      ? orderData.ownItDetailsForRedispatch
      : orderData.ownItDetails;

    if (details) {
      await createPaymentSession(
        orderData.order.customerUuid,
        orderData.order.uuid,
        details.rentalTotalToPayToday.toString(),
        isRequireRedispatch
      );
    }
  };

  // Create payment session when component mounts or when order data is loaded
  useEffect(() => {
    if (orderData?.order?.customerUuid && orderData?.order?.uuid) {
      handlePaymentSession(selectedOption);
    }
  }, [orderData, selectedOption]);

  const handleToggleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectedOption(isChecked ? 'new' : 'preused');

    if (orderData?.order?.customerUuid && orderData?.order?.uuid) {
      handlePaymentSession(selectedOption);
    }
  };

  const handlePayNow = async () => {
    if (!paymentSession?.token) {
      toast.error('Payment session is not ready. Please try again.');
      return;
    }

    try {
      // Show loading state
      const loadingToast = toast.loading('Processing your payment...');
      
      try {
        if (paymentSession?.token) {
          toast.dismiss(loadingToast);
          navigate(`/payment?session=${encodeURIComponent(paymentSession.token)}`);
        } else {
          throw new Error('Payment session token is missing');
        }
      } catch (error) {
        toast.dismiss(loadingToast);
        throw error; // Re-throw to be caught by the outer catch
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to process payment. Please try again.');
    }
  };

  const createPaymentSession = async (
    customerUuid: string,
    orderUuid: string,
    amount: string,
    isRequireRedispatch: boolean = false
  ): Promise<PaymentSessionResponse | null> => {
    try {
      const response = await HttpClient.post<PaymentSessionResponse>(
        API_ENDPOINTS.CREATE_PAYMENT_SESSION,
        {
          customerUuid,
          orderUuid,
          amount,
          isPurchased: 'yes',
          isRequireRedispatch
        }
      );

      if (response) {
        setPaymentSession(response);
        return response;
      }
      return null;
    } catch (error) {
      console.error('Error creating payment session:', error);
      toast.error('Failed to create payment session');
      return null;
    }
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      // ID check is now handled by the top-level effect

      try {
        setLoading(true);
        setError(null);

        // Remove the ID from the URL without reloading the page
        window.history.replaceState({}, document.title, '/complete-purchase');

        // Fetch order details
        const response = await HttpClient.post<OrderResponse>(API_ENDPOINTS.GET_PURCHASE_ORDER, {
          orderUuid: id
        });

        if (response && response.data) {
          console.log('Order data:', response.data);
          setOrderData(response.data);

          // Just set the order data, payment session will be created when user clicks Pay Now
        } else {
          // If no data in response, redirect to 404
          navigate('/not-found');
        }
      } catch (err: any) {
        console.error('Error fetching order details:', err);
        // On error, redirect to 404
        navigate('/not-found');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrderDetails();
    }
  }, [id]);

  // Log payment session for debugging
  useEffect(() => {
    if (paymentSession) {
      console.log('Payment session created:', paymentSession);
    }
  }, [paymentSession]);

  return (
    <section className="cart-section section-space">
      <div className="container">
        <div className="row">
          <div className="offset-xxl-0 col-xxl-8 offset-xl-0 col-xl-7 offset-md-1 col-md-10 ">
            <div className="cart-product-section product-box-section mb-5">
              <div className="cart-product-title">
                <h4>{t("products-details")}</h4>
                <h4>{t("total")}</h4>
              </div>

              <div
                className="cart-product-details-boxs"
              >
                {loading ? (
                  <div className="text-center p-4">Loading order details...</div>
                ) : error ? (
                  <div className="alert alert-danger">{error}</div>
                ) : orderData?.orderProducts?.length ? (
                  orderData.orderProducts.map((product, index) => (
                    <div key={product.uuid || index} className="cart-product-details">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <div className="d-flex align-items-center cart-product-name">
                          <Link
                            to={`/product/?id=${product.productUuid || ''}`}
                            className="c-product-title"
                          >
                            {product.productName || 'Product'}
                          </Link> {product.productCondition == 1 && <span>Pre-used</span>}
                        </div>
                        <h5 className="product-price monthly-product-price">
                          £{product.productRetailValue || '0.00'}
                        </h5>
                      </div>
                      {product.option1 && (
                        <div className="text-muted small">{product.option1}</div>
                      )}
                      {product.option2 && (
                        <div className="text-muted small">{product.option2}</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4">No products found in this order</div>
                )}
              </div>
            </div>
          </div>

          <div className="offset-xxl-0 col-xxl-4 offset-xl-0 col-xl-5 offset-md-1 col-md-10 ">

            <div className="order-summary-position">
              {/* className="cart-chnages-page" */}
              <div className="effect-add-section cart-page-effect">
                <Effect className="effect-img" />
                {/*  */}

                <div className="text-center flex-grow-1 ownit-title-section">
                  <h2 className="ownit-title mb-1">Try it, love it. <span className="ownit-text-black">OWN<span>it.</span></span></h2>
                  <p className="ownit-subtitle mb-3">Try-before-you-buy.</p>

                </div>

                {!orderData?.isAvailableForRedispatch && (
                  <div className="ownit-hero p-4 rounded-3">
                    <div className="d-flex flex-column  align-items-center gap-4">

                      <div className="d-flex align-items-start gap-3">
                        <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.4607 4.96861C21.9102 4.41788 21.2566 3.981 20.5372 3.68293C19.8179 3.38486 19.0468 3.23145 18.2682 3.23145C17.4895 3.23145 16.7184 3.38486 15.9991 3.68293C15.2797 3.981 14.6261 4.41788 14.0757 4.96861L12.9332 6.11104L11.7908 4.96861C10.6789 3.85669 9.17078 3.23202 7.59829 3.23202C6.02579 3.23202 4.5177 3.85669 3.40578 4.96861C2.29386 6.08054 1.66919 7.58862 1.66919 9.16112C1.66919 10.7336 2.29386 12.2417 3.40578 13.3536L4.54821 14.4961L12.9332 22.8811L21.3182 14.4961L22.4607 13.3536C23.0114 12.8031 23.4483 12.1496 23.7463 11.4302C24.0444 10.7108 24.1978 9.93979 24.1978 9.16112C24.1978 8.38245 24.0444 7.6114 23.7463 6.89204C23.4483 6.17268 23.0114 5.51909 22.4607 4.96861Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <div>
                          <h6 className="mb-1">Enjoy your trial</h6>
                          <p className="mb-0 fs-13">Receive a discount when you buy it, or return.</p>
                        </div>
                      </div>
                      <div className="ownit-pricing-card mt-2 mt-lg-0">
                        {orderData?.ownItDetails?.discountPercentage && (
                          <div className="discount-pill">
                            Get {orderData.ownItDetails.discountPercentage}% discount
                          </div>
                        )}
                        <div className="pricing-body p-3 pt-4 rounded">
                          <div className="d-flex justify-content-between mb-2">

                            <label
                              htmlFor="optional-price-after-trial"
                              className="form-check-label"
                            >
                              {t("retail-price")}
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-op`}>
                                    <div dangerouslySetInnerHTML={{ __html: t("retail-price-tooltip") }} />
                                  </Tooltip>
                                }
                              >
                                <Info className="info-icon ms-2" size={18} />
                              </OverlayTrigger>
                            </label>
                            <div className="fw-semibold">
                              £{orderData?.ownItDetails?.productRetailPrice?.toFixed(2) || '0.00'}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <div>Buy now, OWNit price</div>
                            <div className="fw-semibold">
                              £{orderData?.ownItDetails?.ownItPrice?.toFixed(2) || '0.00'}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <label
                              htmlFor="optional-price-after-trial"
                              className="form-check-label"
                            >
                              {t("deposite-paid")}
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-op`}>
                                    <div dangerouslySetInnerHTML={{ __html: t("deposite-paid-tooltip") }} />
                                  </Tooltip>
                                }
                              >
                                <Info className="info-icon ms-2" size={18} />
                              </OverlayTrigger>
                            </label>
                            <div className="fw-semibold text-decoration-line-through">
                              £{orderData?.ownItDetails?.depositPaid?.toFixed(2) || '0.00'}
                            </div>
                          </div>

                        </div>

                      </div>
                    </div>
                  </div>
                )}

                {orderData?.isAvailableForRedispatch && (
                  <div>
                    <div className="purchase-ui py-4">
                      <div className="purchase-ui__card mx-auto ownit-hero">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="purchase-ui__title">Choose your purchase option</div>

                        </div>

                        <label className="purchase-ui__option mb-3 pt-4">
                          {orderData?.ownItDetails?.discountPercentage && (
                            <div className="purchase-ui__discount">
                              Get {orderData.ownItDetails.discountPercentage}% discount
                            </div>
                          )}
                          <div className="d-flex gap-2 align-items-start">
                            <input
                              className="form-check-input purchase-ui__radio mt-1"
                              type="radio"
                              name="purchase"
                              checked={selectedOption === 'preused'}
                              onChange={() => setSelectedOption('preused')}
                              onClick={(e) => e.stopPropagation()}
                            />

                            <div className="flex-grow-1">

                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="purchase-ui__option-title">
                                  Keep Pre-used Product
                                </div>
                                <div className="purchase-ui__badge">
                                  YOUR TRIAL ITEM
                                </div>
                              </div>

                              <div className="purchase-ui__desc mb-2">
                                Continue with the item you've been trying.<br />
                                A great value option.
                              </div>

                              <div className="purchase-ui__price-details">
                                <div className="purchase-ui__price-list mb-1">
                                  <div className="d-flex justify-content-between purchase-ui__price-row">
                                    <span>Retail price</span>
                                    <span>£{orderData?.ownItDetailsForRedispatch?.productRetailPrice?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  <div className="d-flex justify-content-between purchase-ui__price-row">
                                    <span>Pre-used Product Discount</span>
                                    <span>
                                      - £{(
                                        (orderData?.ownItDetailsForRedispatch?.productRetailPrice ?? 0) - 
                                        (orderData?.ownItDetails?.productRetailPrice ?? 0)
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between purchase-ui__price-row">
                                    <span>Buy now, OWNit price</span>
                                    <span>£{orderData?.ownItDetails?.ownItPrice?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  <hr className='purchase-ui__hr'></hr>
                                  <div className="d-flex justify-content-between purchase-ui__price-row is-muted">
                                    <span>Deposit paid</span>
                                    <span className='text-decoration-line-through'>− £{orderData?.ownItDetails?.depositPaid?.toFixed(2) || '0.00'}</span>
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between purchase-ui__total">
                                  <span>You Pay</span>
                                  <span>£{orderData?.ownItDetails?.rentalTotalToPayToday?.toFixed(2) || '0.00'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </label>

                        <label className="purchase-ui__option mb-3 pt-4">                          
                          {orderData?.ownItDetailsForRedispatch?.discountPercentage && (
                            <div className="purchase-ui__discount">
                              Get {orderData.ownItDetailsForRedispatch.discountPercentage}% discount
                            </div>
                          )}
                          <div className="d-flex gap-2 align-items-start">
                            <input
                              className="form-check-input purchase-ui__radio mt-1"
                              type="radio"
                              name="purchase"
                              checked={selectedOption === 'new'}
                              onChange={() => setSelectedOption('new')}
                              onClick={(e) => e.stopPropagation()}
                            />

                            <div className="flex-grow-1">

                              <div className="purchase-ui__option-title mb-1">
                                Upgrade to a New Product
                              </div>

                              <div className="purchase-ui__desc mb-2">
                                Get a brand new, unused item for a little extra.
                              </div>
                              <div className="purchase-ui__price-details">
                                <div className="purchase-ui__price-list mb-1">
                                  <div className="d-flex justify-content-between purchase-ui__price-row">
                                    <span>Retail price</span>
                                    <span>£{orderData?.ownItDetailsForRedispatch?.productRetailPrice?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  <div className="d-flex justify-content-between purchase-ui__price-row">
                                    <span>Buy now, OWNit price</span>
                                    <span>£{orderData?.ownItDetailsForRedispatch?.ownItPrice?.toFixed(2) || '0.00'}</span>
                                  </div>
                                  <hr className='purchase-ui__hr'></hr>
                                  <div className="d-flex justify-content-between purchase-ui__price-row is-muted">
                                    <span>Deposit paid</span>
                                    <span className='text-decoration-line-through'>− £{orderData?.ownItDetailsForRedispatch?.depositPaid?.toFixed(2) || '0.00'}</span>
                                  </div>
                                </div>

                                <div className="d-flex justify-content-between purchase-ui__total">
                                  <span>You Pay</span>
                                  <span>£{orderData?.ownItDetailsForRedispatch?.rentalTotalToPayToday?.toFixed(2) || '0.00'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </label>

                      </div>
                    </div>
                  </div>
                )}

                <div className="place-order-btn orange-order-btn mt-3">
                  <Button
                    onClick={handlePayNow}
                    btnText={`Buy now, Pay £${(
                      selectedOption === 'new' && orderData?.ownItDetailsForRedispatch
                        ? orderData.ownItDetailsForRedispatch.rentalTotalToPayToday
                        : orderData?.ownItDetails?.rentalTotalToPayToday || 0
                    ).toFixed(2)}`}
                    className='orange-button'
                    disabled={!paymentSession || loading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompletePurchase;